// components/Export/ExportCompletedOrdersButton.jsx
import React, { useState } from "react"
import { Button, message } from "antd"
import { DownloadOutlined } from "@ant-design/icons"
import * as XLSX from "xlsx"
import { computeMaterialVarianceForExport } from "../../../../utils/calcMaterialVariance"

function formatTs(ts) {
  if (!ts) return ""
  if (ts.seconds != null) return new Date(ts.seconds * 1000).toLocaleString()
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString()
}

function signed(value, decimals = 0) {
  const n = Number(value)
  if (!Number.isFinite(n)) return "—"
  const fixed = decimals > 0 ? n.toFixed(decimals) : String(Math.round(n))
  if (n === 0) return "0"
  return (n > 0 ? "+" : "") + fixed
}

export default function ExportCompletedOrdersButton({
  data = [],
  filenamePrefix = "completed_orders",
  disabled,
  size,
  type,
}) {
  const [busy, setBusy] = useState(false)

  const handleExport = async () => {
    if (!data.length) return
    setBusy(true)
    try {
      const rows = await Promise.all(
        data.map(async (r) => {
          const manufacturerPrefix =
            r?.manufacturer?.invoicePrefix ?? r?.client?.manufacturer?.invoicePrefix ?? ""
          const manufacturerCompany =
            r?.manufacturer?.company ?? r?.client?.manufacturer?.company ?? ""

          // Same variance calc as UI
          const v = await computeMaterialVarianceForExport(r)

          // Defaults (for Tape / split / no-data)
          let varianceMeters = "—"
          let varianceAreaM2 = "—"
          let varianceCostEUR = "—"

          if (v?.kind === "ok") {
            const { deltaM, deltaM2, costDelta } = v
            // meters: signed, rounded integer
            varianceMeters = signed(deltaM, 0)
            // area (m²): signed, 2 decimals
            varianceAreaM2 = signed(deltaM2, 2)
            // cost: use sign based on deltaM (same as UI), costDelta is absolute
            const costSign = deltaM > 0 ? "+" : deltaM < 0 ? "-" : ""
            varianceCostEUR = costSign ? `${costSign}${costDelta.toFixed(2)}` : "0.00"
          }

          return {
            OrderNumber: r?.orderNumber || "",
            Client: r?.client?.client || "",
            Product: r?.selectedProduct?.product?.productName || "",
            ManufacturerPrefix: manufacturerPrefix,
            ManufacturerCompany: manufacturerCompany,
            // ⬇️ take amount created from the order itself
            AmountCreated: r?.amountCreated ?? "",
            VarianceMeters: varianceMeters,
            VarianceArea_m2: varianceAreaM2,
            VarianceCost_EUR: varianceCostEUR,
            StartedAt: formatTs(r?.created),
            CompletedAt: formatTs(r?.completedTime),
            Machine: r?.selectedProduct?.machine || "",
            SplitOrder: r?.splitOrder ? "Yes" : "No",
            InvoiceURL: r?.invoice?.invoiceUrl || "",
          }
        })
      )

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Completed Orders")

      // Basic widths so it’s readable
      ws["!cols"] = Object.keys(rows[0] || {}).map(() => ({ wch: 22 }))

      const stamp = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14)
      XLSX.writeFile(wb, `${filenamePrefix}_${stamp}.xlsx`)
    } catch (e) {
      console.error(e)
      message.error("Failed to export Excel.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      icon={<DownloadOutlined />}
      onClick={handleExport}
      loading={busy}
      disabled={disabled || !data.length}
      size={size}
      type={type}>
      Export Excel
    </Button>
  )
}
